import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import LoopOutlinedIcon from '@mui/icons-material/LoopOutlined';
import { Alert, Box, Button, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import {
  useCreatePriorAuthorizationCourseMutation,
  useGetPriorAuthorizationCoursesQuery,
  useUpdatePriorAuthorizationCourseMutation,
} from '../store/api';
import type {
  PatientCoverage,
  PriorAuthorizationCourse,
  PriorAuthorizationUpdateRequest,
} from '../types';

import {
  PriorAuthorizationCourseCard,
  type PriorAuthorizationCourseFormValues,
  type PriorAuthorizationCoverageOption,
} from './PriorAuthorizationCourseCard';

interface PriorAuthorizationCoursesSectionProps {
  coverages: PatientCoverage[];
  /** Undefined shows all records, null shows tracking-only records. */
  coverageId?: string | null;
  /** When present, show courses linked to any of these coverages. */
  coverageIds?: string[];
  defaultKind?: PriorAuthorizationCourse['kind'];
  description?: React.ReactNode;
  editable?: boolean;
  emptyMessage?: React.ReactNode;
  heading?: string;
  onCourseSave?: () => void;
  onAdd?: () => void;
  onEdit?: (courseId: string) => void;
  onWorkflowStateChange?: (state: {
    hasOpenForm: boolean;
    isDirty: boolean;
    isSaving: boolean;
  }) => void;
  patientId: string;
  startCreating?: boolean;
  startEditingCourseId?: string | null;
  subjectLabel: string;
}

interface PriorAuthorizationWorkspaceSectionProps {
  coverages: PatientCoverage[];
  currentPrimaryCoverageId?: string;
  editable?: boolean;
  onAdd: () => void;
  onEdit?: (courseId: string) => void;
  patientId: string;
  subjectLabel: string;
}

function errorMessage(error: unknown, fallback: string): string {
  const message = (error as { data?: { message?: unknown } })?.data?.message;
  return typeof message === 'string' && message.trim() ? message : fallback;
}

function coverageName(coverage: PatientCoverage): string {
  const name = coverage.payer_name || coverage.plan_name || 'Insurance coverage';
  const position =
    coverage.coverage_order === 1
      ? 'Primary'
      : coverage.coverage_order === 2
        ? 'Secondary'
        : 'Tertiary';
  return `${name} · ${position}`;
}

export const PriorAuthorizationCoursesSection: React.FC<PriorAuthorizationCoursesSectionProps> = ({
  coverages,
  coverageId,
  coverageIds,
  defaultKind = 'authorization',
  description,
  editable = true,
  emptyMessage,
  heading = 'Prior authorizations',
  onCourseSave,
  onAdd,
  onEdit,
  onWorkflowStateChange,
  patientId,
  startCreating = false,
  startEditingCourseId,
  subjectLabel,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const headingId = useId();
  const { priorAuthorizationCourse: analytics } = useEventTracking();
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const startedCreatingRef = useRef(false);
  const startedEditingRef = useRef<string | null>(null);
  const { data, error, isLoading, isFetching, refetch } = useGetPriorAuthorizationCoursesQuery({
    patientId,
  });
  const [createCourse, { isLoading: isCreating }] = useCreatePriorAuthorizationCourseMutation();
  const [updateCourse, { isLoading: isUpdating }] = useUpdatePriorAuthorizationCourseMutation();

  const coverageOptions = useMemo<PriorAuthorizationCoverageOption[]>(
    () =>
      coverages.map(coverage => ({
        id: coverage.patient_insurance_coverage_id,
        label: coverageName(coverage),
      })),
    [coverages],
  );
  const coverageLabels = useMemo(
    () => new Map(coverageOptions.map(option => [option.id, option.label])),
    [coverageOptions],
  );
  const courses = useMemo(() => {
    const items = data?.items ?? [];
    if (coverageIds !== undefined) {
      const included = new Set(coverageIds);
      return items.filter(course => course.coverage_id && included.has(course.coverage_id));
    }
    if (coverageId === undefined) return items;
    return items.filter(course => course.coverage_id === coverageId);
  }, [coverageId, coverageIds, data?.items]);

  useEffect(() => {
    if (startCreating && editable && !isLoading && !error && !startedCreatingRef.current) {
      startedCreatingRef.current = true;
      setCreating(true);
    }
  }, [editable, error, isLoading, startCreating]);

  useEffect(() => {
    if (
      startEditingCourseId &&
      editable &&
      !isLoading &&
      !error &&
      courses.some(course => course.course_id === startEditingCourseId) &&
      startedEditingRef.current !== startEditingCourseId
    ) {
      startedEditingRef.current = startEditingCourseId;
      setEditingId(startEditingCourseId);
    }
  }, [courses, editable, error, isLoading, startEditingCourseId]);

  useEffect(() => {
    onWorkflowStateChange?.({
      hasOpenForm: creating || editingId !== null,
      isDirty,
      isSaving: isCreating || isUpdating,
    });
  }, [creating, editingId, isCreating, isDirty, isUpdating, onWorkflowStateChange]);

  const handleCreate = async (values: PriorAuthorizationCourseFormValues) => {
    setSaveError(null);
    analytics.saveSubmitted({
      mode: 'create',
      course_kind: values.kind ?? 'authorization',
      has_coverage: Boolean(values.coverage_id),
    });
    try {
      await createCourse({
        priorAuthorizationCreateRequest: {
          ...values,
          patient_id: patientId,
        },
      }).unwrap();
      await refetch();
      setCreating(false);
      setIsDirty(false);
      enqueueSnackbar('Authorization course added', { variant: 'success' });
      onCourseSave?.();
    } catch (caught) {
      setSaveError(errorMessage(caught, 'Could not add authorization course'));
    }
  };

  const handleUpdate = async (
    course: PriorAuthorizationCourse,
    values: PriorAuthorizationCourseFormValues,
  ) => {
    setSaveError(null);
    analytics.saveSubmitted({
      mode: 'edit',
      course_kind: values.kind ?? course.kind,
      has_coverage: Boolean(values.coverage_id),
    });
    const priorAuthorizationUpdateRequest: PriorAuthorizationUpdateRequest = {
      auth_number: values.auth_number,
      authorized_quantity: values.authorized_quantity,
      quantity_unit: values.quantity_unit,
      auth_start_date: values.auth_start_date,
      auth_end_date: values.auth_end_date,
      coverage_id: values.coverage_id,
      cpt_codes: values.cpt_codes,
      kind: values.kind,
      status: values.status,
    };
    try {
      await updateCourse({
        authorizationId: course.id,
        priorAuthorizationUpdateRequest,
      }).unwrap();
      await refetch();
      setEditingId(null);
      setIsDirty(false);
      enqueueSnackbar('Authorization course updated', { variant: 'success' });
      onCourseSave?.();
    } catch (caught) {
      setSaveError(errorMessage(caught, 'Could not update authorization course'));
    }
  };

  return (
    <Box component="section" aria-labelledby={headingId}>
      <Stack spacing={2}>
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
        >
          <Box>
            <Typography id={headingId} variant="h6">
              {heading}
            </Typography>
            <Typography color="text.secondary" variant="body2">
              {description ??
                `Track approved services and utilization for this ${subjectLabel.toLowerCase()}.`}
            </Typography>
          </Box>
          {editable && !creating ? (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                analytics.addClicked();
                if (onAdd) {
                  onAdd();
                  return;
                }
                setSaveError(null);
                setCreating(true);
                setEditingId(null);
              }}
            >
              Add course
            </Button>
          ) : null}
        </Stack>

        {isLoading ? (
          <Stack spacing={1} aria-label="Loading authorization courses">
            <Skeleton variant="rounded" height={104} />
            <Skeleton variant="rounded" height={104} />
          </Stack>
        ) : error ? (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  analytics.retryClicked();
                  void refetch();
                }}
              >
                Retry
              </Button>
            }
          >
            Could not load authorization courses.
          </Alert>
        ) : (
          <Stack spacing={2} aria-busy={isFetching}>
            {courses.length === 0 && !creating ? (
              <Alert severity="info">
                {emptyMessage ??
                  'No prior authorization is recorded yet. Add one to track approved care and claim usage.'}
              </Alert>
            ) : null}

            {courses.map(course => {
              const editing = editingId === course.course_id;
              return (
                <PriorAuthorizationCourseCard
                  key={`${course.course_id}-${editing ? 'edit' : 'summary'}`}
                  course={course}
                  coverageLabel={
                    (course.coverage_id && coverageLabels.get(course.coverage_id)) ||
                    'No linked coverage · tracking only'
                  }
                  coverageOptions={coverageOptions}
                  errorMessage={editing ? saveError : null}
                  isSaving={editing && isUpdating}
                  mode={editing ? 'edit' : 'summary'}
                  subjectLabel={subjectLabel}
                  onCancel={() => {
                    setEditingId(null);
                    setIsDirty(false);
                    setSaveError(null);
                  }}
                  onEdit={
                    editable
                      ? () => {
                          analytics.editClicked({ course_kind: course.kind });
                          if (onEdit) {
                            onEdit(course.course_id);
                            return;
                          }
                          setCreating(false);
                          setIsDirty(false);
                          setSaveError(null);
                          setEditingId(course.course_id);
                        }
                      : undefined
                  }
                  onSubmit={values => void handleUpdate(course, values)}
                  onDirtyChange={setIsDirty}
                />
              );
            })}

            {creating ? (
              <PriorAuthorizationCourseCard
                key="create-course"
                coverageOptions={coverageOptions}
                defaultCoverageId={coverageId}
                defaultKind={defaultKind}
                errorMessage={saveError}
                isSaving={isCreating}
                mode="create"
                subjectLabel={subjectLabel}
                onCancel={() => {
                  setCreating(false);
                  setIsDirty(false);
                  setSaveError(null);
                }}
                onDirtyChange={setIsDirty}
                onSubmit={values => void handleCreate(values)}
              />
            ) : null}
          </Stack>
        )}
      </Stack>
    </Box>
  );
};

function CompactAuthorizationRow({
  editable,
  label,
  onAdd,
}: {
  editable: boolean;
  label: string;
  onAdd: () => void;
}) {
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, px: 2, py: 1.5 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minWidth: 0 }}>
          <FactCheckOutlinedIcon color="action" sx={{ fontSize: 20 }} />
          <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
          <Typography color="text.secondary" variant="body2">
            None recorded
          </Typography>
        </Stack>
        {editable ? (
          <Button size="small" startIcon={<AddIcon />} onClick={onAdd}>
            Add
          </Button>
        ) : null}
      </Stack>
    </Paper>
  );
}

/**
 * Compact production summary for prior authorizations and tracking-only limits.
 * It expands only the groups that contain records and routes empty-state adds
 * into the shared coverage-task dialog.
 */
export const PriorAuthorizationWorkspaceSection: React.FC<
  PriorAuthorizationWorkspaceSectionProps
> = ({
  coverages,
  currentPrimaryCoverageId,
  editable = true,
  onAdd,
  onEdit,
  patientId,
  subjectLabel,
}) => {
  const { priorAuthorizationCourse: analytics } = useEventTracking();
  const { data, error, isLoading, refetch } = useGetPriorAuthorizationCoursesQuery({ patientId });
  const courses = data?.items ?? [];
  const relevantCoverageIds = new Set(
    coverages
      .filter(coverage => ['active', 'future', 'pending'].includes(coverage.status))
      .map(coverage => coverage.patient_insurance_coverage_id),
  );
  if (currentPrimaryCoverageId) relevantCoverageIds.add(currentPrimaryCoverageId);
  const linkedCourses = courses.filter(
    course => course.coverage_id && relevantCoverageIds.has(course.coverage_id),
  );
  const benefitLimits = courses.filter(course => course.coverage_id === null);

  if (isLoading) {
    return (
      <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
        <Stack spacing={1} aria-label="Loading prior authorizations and benefit limits">
          <Skeleton width="42%" />
          <Skeleton width="68%" />
        </Stack>
      </Paper>
    );
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button
            color="inherit"
            size="small"
            onClick={() => {
              analytics.retryClicked();
              void refetch();
            }}
          >
            Retry
          </Button>
        }
      >
        Could not load prior authorizations and benefit limits.
      </Alert>
    );
  }

  if (!linkedCourses.length && !benefitLimits.length) {
    return (
      <CompactAuthorizationRow
        editable={editable}
        label="Prior authorizations & benefit limits"
        onAdd={onAdd}
      />
    );
  }

  return (
    <Stack spacing={1.5}>
      {linkedCourses.length ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
          <PriorAuthorizationCoursesSection
            coverages={coverages}
            coverageIds={[...relevantCoverageIds]}
            description="Track payer-approved services and remaining usage for this plan."
            editable={editable}
            heading="Prior authorizations"
            onAdd={onAdd}
            onEdit={onEdit}
            patientId={patientId}
            subjectLabel={subjectLabel}
          />
        </Paper>
      ) : (
        <CompactAuthorizationRow editable={editable} label="Prior authorizations" onAdd={onAdd} />
      )}

      {benefitLimits.length ? (
        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
          <Stack direction="row" spacing={1} sx={{ alignItems: 'flex-start' }}>
            <LoopOutlinedIcon color="primary" sx={{ mt: 0.5 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <PriorAuthorizationCoursesSection
                coverages={coverages}
                coverageId={null}
                defaultKind="benefit_limit"
                description="Track limits that are not issued by one insurance plan."
                editable={editable}
                heading="Benefit limits"
                onAdd={onAdd}
                onEdit={onEdit}
                patientId={patientId}
                subjectLabel={subjectLabel}
              />
            </Box>
          </Stack>
        </Paper>
      ) : (
        <CompactAuthorizationRow editable={editable} label="Benefit limits" onAdd={onAdd} />
      )}
    </Stack>
  );
};
